import { useState } from 'react';
import { useApp } from '../store';
import { useSampleInert } from './SampleStrip';
import type { Signoff } from '../types';
import { asTime } from '../change-request';
import { asDate } from '../utils';
import { displayName } from '../people';
import Icon from './Icon';

/** What is being recorded, and for whom. Null when nobody is being answered for.
    `cover` is set when the reader is signing as the signatory's delegated cover
    (0130/0131) rather than as themselves or as an admin on their behalf. */
type Answering = { id: string; name: string; answer: 'approved' | 'rejected'; mine: boolean; cover: boolean };

/**
 * THE SIGNATURES ON A SHEET, AND THE ONE WAY A MISSING ONE IS RECORDED.
 *
 * Lifted out of SignOff when documents arrived — #193. A release and a
 * document are signed by the same people in the same way, and the sheet is
 * where every audit property lives: the order is seniority, the position is
 * copied rather than referenced, and a row somebody else recorded says so on
 * its own face.
 *
 * WHAT IS NOT HERE IS DELIBERATE. Each screen keeps its own sentence about
 * what the signatures mean — a release goes live, a document becomes signed
 * off — because those are different questions with different guards. Only the
 * rows are the same thing.
 */
export default function SignatureRows({
  rows,
  mayActFor,
  maySendLink = false,
  locked,
  verb = 'approve',
}: {
  rows: Signoff[];
  /** Whether this reader may record a signature for somebody who could not. */
  mayActFor: boolean;
  /** The word for what a signature means here — a test script is "signed off"
   *  (matching its signing page), everything else "approves". Labels only; the
   *  stored answer stays approved/rejected. */
  verb?: 'approve' | 'signoff';
  /**
   * Whether this reader may hand a signatory a link of their own — #216. Off
   * for a release, whose signatories all have accounts; on for a document,
   * which is the thing a client's sponsor is asked to approve.
   */
  maySendLink?: boolean;
  /**
   * Nothing more can be recorded here — a withdrawn release, a superseded
   * document. The rows still show, because what was signed still happened.
   */
  locked: boolean;
}) {
  const inert = useSampleInert();
  const [answering, setAnswering] = useState<Answering | null>(null);
  /* The link just issued, and whose row it belongs to. Held here because it is
     shown ONCE — only its hash is stored, so leaving this screen loses it. */
  const [link, setLink] = useState<
    | { id: string; url: string; problem: string; emailed: boolean; to: string; why: string }
    | null
  >(null);
  const [sending, setSending] = useState('');
  const issueSigningLink = useApp((s) => s.issueSigningLink);

  const makeLink = async (id: string) => {
    setSending(id);
    setLink(null);
    const got = await issueSigningLink(id);
    setSending('');
    setLink({ id, ...got });
  };
  /*
   * WHOSE ROWS ARE MINE. Being named on the panel is the permission to answer
   * that row, whatever the rank — see 0055. Held here rather than passed in,
   * because it is a property of each ROW and not of the reader.
   */
  const me = useApp((s) => (s.user?.email ?? '').trim().toLowerCase());
  const profiles = useApp((s) => s.profiles);
  const covers = useApp((s) => s.signoffCover);
  const isMine = (row: Signoff) =>
    !!me && (row.signatoryEmail ?? '').trim().toLowerCase() === me;
  /* The active, unexpired cover for the person a row names — the nominee who may
     sign it while they are away (0130). Null when nobody covers them. A row with
     no account behind it cannot be covered: there is no address to match. */
  const nowTs = Date.now();
  const coverFor = (row: Signoff) => {
    const sig = (row.signatoryEmail ?? '').trim().toLowerCase();
    if (!sig) return null;
    return (
      covers.find(
        (c) => c.active && c.delegator === sig && (c.until == null || c.until >= nowTs)
      ) ?? null
    );
  };

  return (
    <table className="so-rows">
      <thead>
        <tr>
          <th>Title</th>
          <th>Name</th>
          <th>Answer</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const open = answering?.id === row.id;
          const mine = isMine(row);
          /* Who, if anyone, covers this row's signatory — and whether that nominee
             is me (so I may sign it as their cover). Cover takes precedence over an
             admin's on-behalf path for me, because it needs no typed reason. */
          const cover = coverFor(row);
          const iCover = !!cover && !!me && cover.delegate === me && !mine;
          return [
            <tr key={row.id} className={open ? 'is-answering' : undefined}>
              <td className="so-role">{row.position}</td>
              <td className="so-who">
                {row.name}
                {/* SAID ON THE SHEET, not only implied by which row has buttons.
                    Somebody scanning four rows for the one they owe an answer on
                    should not have to work it out from what is clickable. */}
                {mine && <span className="so-mine">you</span>}
                {/* AN EXTERNAL SIGNER, MARKED ON THE SHEET — somebody with no
                    account who answered through their own link (a client's
                    sponsor, or the UAT tester who completed a test script). Read
                    in form, not only by position, so the sheet says who is outside
                    the team. Shown once they have answered via the link, which is
                    when it is certain; `answered_via` is the proof. */}
                {row.answeredVia === 'link' && (
                  <span className="so-ext" title="External signer — no account here; answered through their own link">
                    <Icon name="building" size={11} />
                    External
                  </span>
                )}
                {/* THE WORD, NOT ONLY THE ABSENCE OF ONE. A row signed by
                    somebody other than the person named has to say so on the
                    sheet itself, or the sheet claims more than it can prove. */}
                {row.onBehalfBy && (
                  <em className="so-behalf">
                    recorded by {displayName(row.onBehalfBy, profiles)} — {row.onBehalfReason}
                  </em>
                )}
                {/* WHO MAY STAND IN — said on the sheet, so the person chasing a
                    signature knows it can still be given while the signatory is
                    away. Only on an unanswered row, and not once it has been
                    recorded (the line above says who then). */}
                {!row.signedAt && !row.onBehalfBy && cover && (
                  <em className="so-behalf so-delegated">
                    {iCover
                      ? 'You may sign this — delegated cover'
                      : `${displayName(cover.delegate, profiles)} may sign — delegated cover`}
                  </em>
                )}
                {/*
                  * SEEN, BEFORE IT IS ANSWERED — Vinnie's question, 28 Sep:
                  * "can someone else see that it has been seen?" The sign page
                  * stamps `opened_at` the first time the approver opens the thing
                  * (sign/index.ts), and until now that was read by nobody. On an
                  * unanswered row it is the one thing the person who asked wants:
                  * the link was opened, so it arrived and was read — the answer is
                  * being weighed, not lost in a spam folder. Once the row is
                  * signed the answer and its time say more, so this gives way to
                  * it.
                  */}
                {!row.signedAt && !locked && row.openedAt && (
                  <span
                    className="so-seen"
                    title={`Opened ${asDate(row.openedAt)} · ${asTime(row.openedAt)}`}
                  >
                    Opened {asDate(row.openedAt)}
                  </span>
                )}
              </td>
              <td className="so-when">
                {/*
                  * WHAT THEY SAID, NOT ONLY THAT THEY ANSWERED.
                  *
                  * A refusal used to look exactly like a signature that had not
                  * arrived, so nobody chasing one could tell "on leave" from
                  * "said no". CPFC's own sheet keeps Approved and Rejected as
                  * two columns beside the signature, and this is the same idea
                  * in the room a screen has.
                  *
                  * The reason is shown beside it rather than hidden behind the
                  * row, because a no whose reason has to be hunted for is the
                  * work the sheet was supposed to save.
                  */}
                {row.signedAt ? (
                  row.answer === 'rejected' ? (
                    <>
                      <span className="so-pill is-rejected">
                        {verb === 'signoff' ? 'Didn’t pass' : 'Rejected'}
                      </span>{' '}
                      {/* Date AND time — the audit line records when as well as
                          who, so two answers on the same day can be told apart. */}
                      <span className="so-date">
                        {asDate(row.signedAt)} · {asTime(row.signedAt)}
                      </span>
                      {row.why && <em className="so-why">{row.why}</em>}
                    </>
                  ) : (
                    <>
                      <span className="so-pill is-signed">
                        {verb === 'signoff' ? 'Signed off' : 'Approved'}
                      </span>{' '}
                      <span className="so-date">
                        {asDate(row.signedAt)} · {asTime(row.signedAt)}
                      </span>
                    </>
                  )
                ) : (mine || iCover || mayActFor) && !locked ? (
                  /*
                   * THE TWO ANSWERS ARE THE ROW.
                   *
                   * They used to be behind "Record it", which opened a panel
                   * that asked what they said as two quiet chips — so the two
                   * things you are here to do were three presses in and drawn
                   * like labels. Answering is the point of the sheet, so it is
                   * on the sheet.
                   *
                   * NO DEFAULT IS SELECTED. "Approved" pre-chosen is how a
                   * refusal gets recorded as agreement by somebody moving fast.
                   */
                  <span className="so-pick">
                    {(['approved', 'rejected'] as const).map((a) => (
                      <button
                        key={a}
                        aria-pressed={open && answering.answer === a}
                        {...inert(`so-ans is-${a} ${open && answering.answer === a ? 'on' : ''}`, () =>
                          setAnswering({ id: row.id, name: row.name, answer: a, mine, cover: iCover })
                        )}
                      >
                        {a === 'approved'
                          ? verb === 'signoff'
                            ? 'Sign off'
                            : 'Approved'
                          : verb === 'signoff'
                            ? 'Didn’t pass'
                            : 'Rejected'}
                      </button>
                    ))}
                  </span>
                ) : (
                  /* NOT DRAWN AND THEN REFUSED. A reader who cannot answer sees
                     no buttons at all — a control that exists to say no teaches
                     people to stop reading refusals. */
                  <span className="so-pill is-waiting">Waiting</span>
                )}
                {/*
                  * A LINK OF THEIR OWN — #216, for a document, a sprint or a
                  * release alike.
                  *
                  * The people who sign a scope of works have no account and are
                  * never going to be given one, so this is the row where their
                  * answer stops being second-hand. It used to be gated to
                  * documents (`row.documentId`), which left a sprint or a release
                  * approver with no way to be sent a link at all — the reach-
                  * everyone gap. The address is frozen on every row whatever the
                  * subject, so the button belongs on every unsigned one. Beside
                  * the answers rather than behind a menu, because it is what a
                  * manager does FIRST: you send the link, and the answer comes
                  * back by itself.
                  */}
                {maySendLink && !row.signedAt && !locked && (
                  <button
                    disabled={sending === row.id}
                    {...inert('link so-send', () => makeLink(row.id))}
                  >
                    {sending === row.id
                      ? 'Making it…'
                      : row.linkIssuedAt
                        ? 'Send the link again'
                        : 'Send a link'}
                  </button>
                )}
              </td>
            </tr>,
            /* UNDER THE ROW IT BELONGS TO, so whose answer is being recorded is
               never in doubt. It used to open below the whole table. */
            open ? (
              <tr key={`${row.id}-form`} className="so-form-row">
                <td colSpan={3}>
                  <Answer
                    signoffId={row.id}
                    name={row.name}
                    answer={answering.answer}
                    mine={answering.mine}
                    cover={answering.cover}
                    verb={verb}
                    onDone={() => setAnswering(null)}
                  />
                </td>
              </tr>
            ) : link?.id === row.id ? (
              <tr key={`${row.id}-link`} className="so-form-row">
                <td colSpan={3}>
                  <TheLink
                    name={row.name}
                    url={link.url}
                    problem={link.problem}
                    emailed={link.emailed}
                    to={link.to}
                    why={link.why}
                    onDone={() => setLink(null)}
                  />
                </td>
              </tr>
            ) : null,
          ];
        })}
      </tbody>
    </table>
  );
}

/**
 * ANSWERING A ROW — YOUR OWN, OR ONE SOMEBODY ELSE COULD NOT SIGN.
 *
 * ONE COMPONENT, TWO ACTS, and the difference is written on its face rather
 * than hidden in a flag. Vinnie, 22 August: *"if Mikkel and Dominic as the lead
 * dev and dev are the ones doing the work we have them sign off as being
 * accepted... will they be able to sign even as members and a reviewer? This is
 * an important distinction that must be clarified."*
 *
 *   YOUR OWN ROW    being named IS the permission, at any rank. Approving asks
 *                   nothing at all; rejecting asks the one thing a reader needs.
 *                   Nothing about anybody else goes on the row.
 *   SOMEBODY ELSE'S rank 4 and up. The reason is required and the field says why
 *                   it is being asked for. It is not paperwork: it is what an
 *                   auditor reads to decide whether the exception was
 *                   reasonable, and having to write a sentence is the friction
 *                   that keeps this from becoming the ordinary way rows get
 *                   signed.
 *
 * WHO MAY DO THE SECOND IS SAID OUT LOUD, because the screen is the only place
 * it is written down. Managers as well as admins since 0054 — the person
 * running the board could not write down an answer to the thing they
 * administer, which made every signature wait on one person being at a
 * keyboard.
 */
function Answer({
  signoffId,
  name,
  answer,
  mine,
  cover,
  verb,
  onDone,
}: {
  signoffId: string;
  name: string;
  answer: 'approved' | 'rejected';
  /** Whether this row names the person reading it. */
  mine: boolean;
  /** Whether the reader is signing as the signatory's delegated cover (0130/0131). */
  cover: boolean;
  /** The word for the act — "sign off" for a test script, else "approve". */
  verb: 'approve' | 'signoff';
  onDone: () => void;
}) {
  const inert = useSampleInert();
  const signOnBehalf = useApp((s) => s.signOnBehalf);
  const answerMyRow = useApp((s) => s.answerMyRow);
  const signAsCover = useApp((s) => s.signAsCover);
  const [why, setWhy] = useState('');
  const [said, setSaid] = useState('');
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);

  const no = answer === 'rejected';
  /* Approving your own row — or a row you cover — needs no sentence: pressing it IS
     the answer (cover carries its own reason). Only an admin recording for somebody
     must say why, and only a rejection must say what they said. */
  const nothingToAsk = (mine || cover) && !no;
  /* The act in words, kind-aware: a test script is "signed off", everything else
     "approved". Written as the whole phrase (not "{verb} this") so "sign this off"
     reads right. The stored answer is still approved/rejected. */
  const mineVerb = no
    ? verb === 'signoff'
      ? 'mark it as not passed'
      : 'reject this'
    : verb === 'signoff'
      ? 'sign this off'
      : 'approve this';
  const theirVerb = no
    ? verb === 'signoff'
      ? 'marked it as not passed'
      : 'rejected this'
    : verb === 'signoff'
      ? 'signed this off'
      : 'approved this';

  return (
    <div className="so-behalf-form">
      <p className="so-behalf-head">
        {mine ? (
          <>
            Recording that <strong>you</strong> {mineVerb}, as <strong>{name}</strong>.
          </>
        ) : cover ? (
          <>
            Recording that <strong>{name}</strong> {theirVerb}, as their{' '}
            <strong>delegated cover</strong>.{' '}
            <span className="hint">
              You are covering {name} while they are away, so the sheet will show you signed on
              their behalf.
            </span>
          </>
        ) : (
          <>
            Recording that <strong>{name}</strong> {theirVerb}, on their behalf.{' '}
            <span className="hint">
              Only an admin or this project&rsquo;s manager can do this, and the sheet will show
              it was you.
            </span>
          </>
        )}
      </p>

      {/*
        * TWO QUESTIONS ONLY WHEN THERE ARE TWO, AND THEY ARE NOT THE SAME
        * QUESTION. One is what the signatory gave for refusing; the other is
        * why this admin is the one typing it. Asking once would put the admin's
        * words in their mouth, and that is the sentence an auditor reads.
        *
        * An approval needs only the second, and your own needs neither: "they
        * said yes" is the answer in full, and nobody is answering for you.
        */}
      {no && (
        <label className="stack">
          {mine ? 'Why are you rejecting it?' : 'Why did they reject it?'}{' '}
          <span className="req">*</span>
          <input
            className="input"
            value={said}
            autoFocus
            aria-required="true"
            placeholder="e.g. the scope does not cover the cancellation letter"
            onChange={(e) => setSaid(e.target.value)}
          />
        </label>
      )}

      {!mine && !cover && (
        <label className="stack">
          Why can they not answer it themselves? <span className="req">*</span>
          <input
            className="input"
            value={why}
            autoFocus={!no}
            aria-required="true"
            placeholder="e.g. on leave until the 4th — sent it by email"
            onChange={(e) => setWhy(e.target.value)}
          />
        </label>
      )}

      {nothingToAsk && (
        <p className="so-caveat">
          {cover
            ? `Nothing else is asked — your cover for ${name} is the reason, and it is recorded with your signature.`
            : 'This is your own row, so nothing else is asked. Your signature is recorded against your name alone.'}
        </p>
      )}

      {/* NAMED FOR WHAT IT RECORDS. "Record it" sat inches from the document
          screen's "Record it as signed off", which is a different act on a
          different thing, and one of them cannot be undone. */}
      <div className="so-behalf-act">
        <button
          disabled={
            saving || (no && said.trim() === '') || (!mine && !cover && why.trim() === '')
          }
          {...inert('btn btn-small', async () => {
            setSaving(true);
            const problem = mine
              ? await answerMyRow(signoffId, answer, said)
              : cover
                ? await signAsCover(signoffId, answer, said)
                : await signOnBehalf(signoffId, why, answer, said);
            setSaving(false);
            if (problem) setProblem(problem);
            else onDone();
          })}
        >
          {saving
            ? 'Recording…'
            : no
              ? verb === 'signoff'
                ? 'Record that it didn’t pass'
                : 'Record the rejection'
              : verb === 'signoff'
                ? 'Record the sign-off'
                : 'Record the approval'}
        </button>
        <button className="link" onClick={onDone}>
          Cancel
        </button>
      </div>
      {problem && (
        <p className="so-problem" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

/**
 * THE LINK, SHOWN ONCE — #216.
 *
 * Only its hash is stored, so this is the only moment it exists anywhere it can
 * be read. That is deliberate: a table of working links is a thing that can be
 * stolen, and a link that can be looked up later is a link an office full of
 * people can quietly reuse. The cost is that a mislaid one is REPLACED rather
 * than found, and the button says so.
 *
 * FLIGHTDECK POSTS IT, AND THE SCREEN SAYS SO — or says it did not, and shows
 * the address to send by hand. Both are ordinary outcomes: a panel member with
 * no email on the roster is a normal thing, not a fault, and telling somebody
 * "sent" when nothing was sent is the one answer that must never appear.
 */
function TheLink({
  name,
  url,
  problem,
  emailed,
  to,
  why,
  onDone,
}: {
  name: string;
  url: string;
  problem: string;
  emailed: boolean;
  to: string;
  why: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  if (problem)
    return (
      <div className="so-behalf-form">
        <p className="so-problem" role="alert">
          {problem}
        </p>
        <div className="so-behalf-act">
          <button className="link" onClick={onDone}>
            Close
          </button>
        </div>
      </div>
    );
  /* SENT IS THE SHORT CASE. There is nothing for anybody to do, so the link is
     not put on screen at all — a credential shown for no reason is a credential
     somebody copies for no reason. */
  if (emailed)
    return (
      <div className="so-behalf-form">
        <p className="so-behalf-head">
          Sent to <strong>{to}</strong>.{' '}
          <span className="hint">
            {name} can answer from the email. It lasts two weeks, it stops working once the
            request is withdrawn, and sending another replaces it.
          </span>
        </p>
        <div className="so-behalf-act">
          <button className="link" onClick={onDone}>
            Close
          </button>
        </div>
      </div>
    );

  return (
    <div className="so-behalf-form">
      <p className="so-behalf-head">
        {/* WHY IT IS BEING COPIED BY HAND, said in the sentence that asks them
            to. "Send this yourself" with no reason reads as the feature being
            broken, when usually it is a panel row with no address on it. */}
        {why ? <>{why} </> : null}
        <strong>{name}</strong>&rsquo;s link. Send it to them and nobody else.{' '}
        <span className="hint">
          It is the whole credential — whoever holds it can answer this row. It lasts two weeks,
          it stops working once the request is withdrawn, and it is shown only now: sending
          another replaces it.
        </span>
      </p>
      <p className="so-link">{url}</p>
      <div className="so-behalf-act">
        <button
          className="btn btn-small"
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy the link'}
        </button>
        <button className="link" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
